import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STEPS = [
  {
    target: null,
    title: "Welcome to Pravadh GovernX 👋",
    body: "Let's take a 20-second tour of the three things that matter: connecting your systems, governing them, and extracting your data.",
  },
  {
    target: "app-nav-connections",
    title: "1. Connect your systems",
    body: "Start here. Securely connect Salesforce, Zoho or Oracle — credentials are encrypted at rest.",
  },
  {
    target: "app-nav-governance",
    title: "2. Set governance rules",
    body: "Define thresholds and alerts (API limits, storage, failures). We watch them and surface breaches on your Overview.",
  },
  {
    target: "app-nav-extraction",
    title: "3. Extract your data",
    body: "Pick objects (or paste a SOQL query), choose your format, and download a file. Use Jobs when you want that export on a schedule.",
  },
  {
    target: "app-nav-jobs",
    title: "4. Schedule jobs",
    body: "Create a recurring job after you have a connection. Run now from the job list when you want a fresh history row.",
  },
  {
    target: "app-nav-billing",
    title: "5. Billing stays visible",
    body: "Your plan and invoices live here. You will never lose a screen — if something fails, use Try again.",
  },
  {
    target: null,
    title: "You're all set",
    body: "I'll keep a next-step bar at the top until you're set up. EMIE, your co-pilot, lives in the bottom-right — and will speak up on its own when something needs you.",
  },
];

function useTargetRect(testid, step) {
  const [rect, setRect] = useState(null);
  useLayoutEffect(() => {
    if (!testid) {
      setRect(null);
      return;
    }
    const measure = () => {
      const el = document.querySelector(`[data-testid="${testid}"]`);
      setRect(el ? el.getBoundingClientRect() : null);
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [testid, step]);
  return rect;
}

export function WelcomeTour() {
  const { user, loadMe } = useAuth();
  const [show, setShow] = useState(false);
  const [i, setI] = useState(0);
  const finishedRef = useRef(false);

  useEffect(() => {
    if (!user || user === false) return;
    let active = true;
    api
      .get("/onboarding/status")
      .then((r) => {
        if (active && !r.data.tour_done) setShow(true);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [user]);

  const step = STEPS[i];
  const rect = useTargetRect(step?.target, i);

  const finish = async () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    setShow(false);
    try {
      await api.post("/onboarding/complete-tour");
      await loadMe();
    } catch {
      /* ignore */
    }
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("gx-guide-refresh"));
    }
  };

  if (!show || !step) return null;

  const pad = 8;
  const hole = rect
    ? {
        top: rect.top - pad,
        left: rect.left - pad,
        width: rect.width + pad * 2,
        height: rect.height + pad * 2,
      }
    : null;

  // Tooltip position
  let cardStyle = {};
  if (hole) {
    const below = hole.top + hole.height + 14;
    const left = Math.min(Math.max(hole.left, 12), window.innerWidth - 332);
    cardStyle = { top: below, left };
  } else {
    cardStyle = { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  }

  return createPortal(
    <div className="fixed inset-0 z-[200]" data-testid="welcome-tour" aria-live="polite">
      {/* Dim + spotlight */}
      {hole ? (
        <div
          className="pointer-events-none absolute rounded-xl transition-all duration-300"
          style={{
            top: hole.top,
            left: hole.left,
            width: hole.width,
            height: hole.height,
            boxShadow: "0 0 0 9999px rgba(2, 6, 23, 0.6)",
            border: "2px solid rgba(59,130,246,0.9)",
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-slate-950/60" />
      )}

      {/* Tooltip card */}
      <div
        className="absolute w-[320px] max-w-[calc(100vw-24px)] rounded-2xl border border-border bg-background p-5 shadow-2xl"
        style={cardStyle}
        data-testid="welcome-tour-card"
      >
        <div className="mb-2 flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary">
            <Sparkles className="h-3 w-3" /> Quick tour
          </span>
          <button
            onClick={finish}
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="Skip tour"
            data-testid="welcome-tour-skip"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <h3 className="font-display text-lg font-bold text-foreground">{step.title}</h3>
        <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{step.body}</p>

        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            {STEPS.map((_, idx) => (
              <span
                key={idx}
                className={`h-1.5 rounded-full transition-all ${idx === i ? "w-5 bg-primary" : "w-1.5 bg-border"}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {i > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setI((n) => n - 1)} data-testid="welcome-tour-back">
                Back
              </Button>
            )}
            {i < STEPS.length - 1 ? (
              <Button size="sm" onClick={() => setI((n) => n + 1)} data-testid="welcome-tour-next">
                Next
              </Button>
            ) : (
              <Button size="sm" onClick={finish} data-testid="welcome-tour-finish">
                Get started
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
